import { useState, useRef, useEffect, useMemo, useId } from 'react'
import './Combobox.css'

// Normalise pour une recherche sans accents ni casse
const norm = s => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/**
 * Liste deroulante avec saisie pour filtrer.
 * options : [{ value, label }]
 * value : valeur selectionnee ('' = aucune)
 * onChange(value) : appele avec la valeur choisie ('' si on choisit l'option vide)
 * placeholder : libelle de l'option vide ("Choisir...", "A pourvoir"). Si absent, pas d'option vide.
 * className : classe appliquee au champ (reprend le style des anciens select)
 * resetAfterSelect : pour les listes "Affecter..." qui restent toujours vides
 */
export default function Combobox({
  options, value, onChange, placeholder, className = '', disabled = false, resetAfterSelect = false,
}) {
  const [ouvert, setOuvert] = useState(false)
  const [saisie, setSaisie] = useState('')
  const [actif, setActif] = useState(0)
  const racine = useRef(null)
  const champ = useRef(null)
  const idListe = useId()

  const choisi = options.find(o => String(o.value) === String(value))
  const libelleChoisi = choisi ? choisi.label : ''

  const liste = useMemo(() => {
    const q = norm(saisie.trim())
    const filtrees = q ? options.filter(o => norm(o.label).includes(q)) : options
    return placeholder !== undefined && !q
      ? [{ value: '', label: placeholder, vide: true }, ...filtrees]
      : filtrees
  }, [options, saisie, placeholder])

  useEffect(() => { setActif(0) }, [saisie, ouvert])

  // Fermeture au clic en dehors
  useEffect(() => {
    if (!ouvert) return
    function dehors(e) {
      if (racine.current && !racine.current.contains(e.target)) fermer()
    }
    document.addEventListener('mousedown', dehors)
    return () => document.removeEventListener('mousedown', dehors)
  })

  function fermer() {
    setOuvert(false)
    setSaisie('')
  }

  function choisir(o) {
    if (o.value !== value) onChange(o.value)
    fermer()
    if (champ.current) champ.current.blur()
  }

  function touche(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!ouvert) setOuvert(true)
      else setActif(a => Math.min(a + 1, liste.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActif(a => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      if (ouvert) {
        e.preventDefault()
        if (liste[actif]) choisir(liste[actif])
      }
    } else if (e.key === 'Escape') {
      if (ouvert) { e.preventDefault(); fermer() }
    } else if (e.key === 'Tab') {
      fermer()
    }
  }

  const texte = ouvert ? saisie : (resetAfterSelect ? '' : libelleChoisi)
  const hint = ouvert ? (libelleChoisi || placeholder || '') : (placeholder || '')

  return (
    <div className="cb" ref={racine}>
      <input
        ref={champ}
        type="text"
        role="combobox"
        aria-expanded={ouvert}
        aria-controls={idListe}
        autoComplete="off"
        className={`cb-input ${className}`}
        value={texte}
        placeholder={hint}
        disabled={disabled}
        onFocus={() => { setOuvert(true); if (champ.current) champ.current.select() }}
        onClick={() => setOuvert(true)}
        onChange={e => { setSaisie(e.target.value); setOuvert(true) }}
        onKeyDown={touche}
      />
      <span className="cb-fleche" aria-hidden="true">▾</span>
      {ouvert && !disabled && (
        <ul id={idListe} className="cb-liste" role="listbox">
          {liste.length === 0 && <li className="cb-aucun">Aucun résultat</li>}
          {liste.map((o, i) => (
            <li
              key={`${o.value}-${i}`}
              role="option"
              aria-selected={String(o.value) === String(value)}
              className={`cb-option${i === actif ? ' actif' : ''}${String(o.value) === String(value) && !o.vide ? ' choisi' : ''}${o.vide ? ' vide' : ''}`}
              onMouseDown={e => { e.preventDefault(); choisir(o) }}
              onMouseEnter={() => setActif(i)}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
